import React, { useEffect, useState } from 'react';
import { Collapse, Icon, IconButton, ListItem, ListItemText } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { FuseUtils } from '@fuse';
import { withRouter } from 'react-router-dom';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import NavGroup from './FuseNavGroup';
import NavItem from './FuseNavItem';
import NavBadge from './FuseNavBadge';
import NavLink from './FuseNavLink';

const useStyles = makeStyles(theme => ({
	root: {
		padding: 0,
		'&.open': {
			backgroundColor: 'rgba(0,0,0,.08)'
		}
	},
	item: {
		height: 40,
		width: 'calc(100% - 16px)',
		borderRadius: '0 20px 20px 0',
		paddingRight: 12,
		color: theme.palette.text.primary,
		'&.square': {
			width: '100%',
			borderRadius: '0'
		}
	},
	disabledItem: {
		opacity: 0.4,
		cursor: 'not-allowed',
		pointerEvents: 'none',
		'& .MuiIcon-root': {
			color: theme.palette.text.disabled
		},
		'& .MuiListItemText-primary': {
			color: theme.palette.text.disabled
		}
	}
}));

const needsToBeOpened = (location, item) => {
	return location && isUrlInChildren(item, location.pathname);
};

const isUrlInChildren = (parent, url) => {
	if (!parent.children) {
		return false;
	}

	for (let i = 0; i < parent.children.length; i++) {
		if (parent.children[i].children) {
			if (isUrlInChildren(parent.children[i], url)) {
				return true;
			}
		}

		if (parent.children[i].url === url || url.includes(parent.children[i].url)) {
			return true;
		}
	}

	return false;
};

const NavCollapse = props => {
	const userRole = useSelector(({ auth }) => auth.user.role);
	const user = useSelector(({ auth }) => auth.user);

	const classes = useStyles(props);
	const [open, setOpen] = useState(() => needsToBeOpened(props.location, props.item));
	const { item, nestedLevel, active } = props;
	let paddingValue = 40 + nestedLevel * 16;
	const listItemPadding = nestedLevel > 0 ? 'pl-' + (paddingValue > 80 ? 80 : paddingValue) : 'pl-24';

	useEffect(() => {
		if (needsToBeOpened(props.location, props.item)) {
			setOpen(true);
		}
	}, [props.location, props.item]);

	const handleClick = () => setOpen(!open);

	if (!FuseUtils.hasPermission(item.auth, userRole)) return null;

	// Check if any children are visible
	const hasVisibleChildren = (children) => {
		if (!children || children.length === 0) return false;
		
		return children.some(child => {
			// Check basic authorization
			if (!FuseUtils.hasPermission(child.auth, userRole)) {
				// If no auth specified, continue checking
				if (child.auth !== undefined) {
					return false;
				}
			}
			
			// If it's a group or collapse, check if it has visible children
			if (child.type === 'group' || child.type === 'collapse') {
				return hasVisibleChildren(child.children);
			}
			
			// For leaf items, check training_allow for participants
			if (child.type === 'item') {
				if (user.participant && !child.training_allow) {
					return false;
				}
			}
			
			// If we get here, the item is visible
			return true;
		});
	};

	// If no visible children, don't render the collapse
	if (!hasVisibleChildren(item.children)) {
		return null;
	}

	// Check if item should be disabled for current user
	const isDisabled = item.disabled && FuseUtils.hasPermission(item.disabled, userRole);

	return (
		<ul className={clsx(classes.root, open && 'open')}>
			<ListItem
				button
				className={clsx(
					classes.item,
					listItemPadding,
					'list-item',
					active,
					isDisabled && classes.disabledItem
				)}
				onClick={isDisabled ? undefined : handleClick}
				disabled={isDisabled}
			>
				{item.icon && (
					<Icon color="action" className="text-16 flex-shrink-0 mr-16">
						{item.icon}
					</Icon>
				)}
				<ListItemText className="list-item-text" primary={item.title} classes={{ primary: 'text-14' }} />
				{item.badge && <NavBadge className="mr-4" badge={item.badge} />}
				<IconButton disableRipple className="w-16 h-16 p-0" disabled={isDisabled}>
					<Icon className="text-16 arrow-icon" color="inherit">
						{open ? 'expand_less' : 'expand_more'}
					</Icon>
				</IconButton>
			</ListItem>

			{item.children && (
				<Collapse in={open} className="collapse-children">
					{item.children.map(item => (
						<React.Fragment key={item.id}>
							{item.type === 'group' && <NavGroup item={item} nestedLevel={nestedLevel + 1} active={active} />}

							{item.type === 'collapse' && <NavCollapse item={item} nestedLevel={nestedLevel + 1} active={active} />}

							{item.type === 'item' && <NavItem item={item} nestedLevel={nestedLevel + 1} active={active} />}

							{item.type === 'link' && <NavLink item={item} nestedLevel={nestedLevel + 1} active={active} />}
						</React.Fragment>
					))}
				</Collapse>
			)}
		</ul>
	);
};

NavCollapse.propTypes = {
	item: PropTypes.shape({
		id: PropTypes.string.isRequired,
		title: PropTypes.string,
		icon: PropTypes.string,
		children: PropTypes.array
	})
};
NavCollapse.defaultProps = {};

export default withRouter(React.memo(NavCollapse));
